import axios from "axios";
import { getJwtToken, updateJwtToken } from "utils/authUtils";
import { handledGeneralApiError } from "utils/errorRoutingUtils";

/**
 * Builds the Axios instance we need to handle http request and response
 *
 * @param {import("axios").AxiosRequestConfig} axiosConfig - Object containing any request
 * configurations. Click the "AxiosRequestConfig" to see the configurations. At minimum,
 * the baseURL key in this object is required.
 * @param {boolean} includeRequestInterceptor - Whether to include the request interceptor logic.
 *
 * @returns {import("axios").AxiosInstance} Which is used to make the get/post requests
 */

const createAxiosInstance = (axiosConfig, includeRequestInterceptor = true) => {
	if (
		axiosConfig == null ||
		axiosConfig.baseURL == null ||
		axiosConfig.baseURL === ""
	) {
		throw new Error("Configuration must include baseURL");
	}

	// Initialize Axios configurations
	const instance = axios.create({ ...axiosConfig });

	// Conditionally add the request interceptor
	if (includeRequestInterceptor) {
		instance.interceptors.request.use(
			(config) => {
				const jwt = getJwtToken();

				config.headers.Authorization = jwt;

				return config;
			},
			(error) => Promise.reject(error)
		);
	}

	// Interceptor for handling Http response
	instance.interceptors.response.use(
		(res) => {
			const newJwt = res.headers.authorization;

			if (newJwt != null && newJwt !== "") {
				updateJwtToken(newJwt);
			}

			return res;
		},
		(err) =>
			Promise.reject({
				...err,
				isGeneralError: handledGeneralApiError(err), // True if general api error handled
			})
	);

	return instance;
};

export default createAxiosInstance;
